import { useQuery } from "@tanstack/react-query";
import client from "../client";

export interface LatestInvoice {
  id: string;
  number: string | null;
  amountPaid: number;
  amountDue: number;
  status: string | null;
  billingReason: string | null;
  hostedUrl: string | null;
  createdAt: string;
}

interface Response {
  invoices: LatestInvoice[];
}

/**
 * Fetches the most recent finalized Stripe invoices for a customer.
 * Backend filters out drafts (un-finalized placeholders that crowd the
 * list — e.g. a $0 subscription_cycle draft eating a slot that should
 * have surfaced a real same-day commission invoice). Default 5 keeps
 * the list visually tight while still showing both invoices when a
 * customer is billed twice in one day.
 */
export function useLatestInvoices(customerId: string, limit = 5) {
  return useQuery({
    queryKey: ["crm", "latestInvoices", customerId, limit],
    queryFn: () =>
      client
        .get<Response>(`/admin/customers/${customerId}/invoices/latest`, {
          params: { limit },
        })
        .then((r) => r.data.invoices),
    enabled: Boolean(customerId),
    staleTime: 5 * 60 * 1000, // 5 min — invoices don't change often
  });
}
